import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import type { Page } from '@/api/types';
import { StatusBadge } from '@/design-system/DataDisplay';

/**
 * Pieces every Settings section uses.
 *
 * The sections are several small editors over lists that fit in one response, so they share one way
 * of saving - toast, refresh, and a server rejection mapped onto the field it names - rather than
 * each re-implementing it slightly differently.
 */

/** A whole list presented as one page, for the table that expects the server's page envelope. */
export function asPage<T>(items: T[] | undefined): Page<T> | undefined {
  if (!items) return undefined;
  return {
    content: items,
    page: 0,
    size: Math.max(items.length, 1),
    totalElements: items.length,
    totalPages: 1,
    first: true,
    last: true,
  };
}

export function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <StatusBadge tone="success">Active</StatusBadge>
  ) : (
    <StatusBadge tone="neutral">Inactive</StatusBadge>
  );
}

/** A titled block within a section. */
export function SettingsCard({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-h2 text-text-primary">{title}</h2>
          {description && <p className="mt-0.5 text-small text-text-muted">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/**
 * Saving an edit from a Settings dialog.
 *
 * On success: a toast, the affected lists refetched, and the dialog closed. On a rejection naming a
 * field, the message lands on that field; any other failure is returned for the dialog to show.
 */
export function useSettingsSave<TValues extends FieldValues, TResult>({
  save,
  invalidate,
  successMessage,
  setError,
  onDone,
  onOtherError,
}: {
  save: (values: TValues) => Promise<TResult>;
  invalidate: readonly (readonly unknown[])[];
  successMessage: string;
  setError: UseFormSetError<TValues>;
  onDone: () => void;
  onOtherError: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: save,
    onSuccess: () => {
      invalidate.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
      toast.success(successMessage);
      onDone();
    },
    onError: (error) => {
      // The server names the field either in fieldErrors (bean validation) or in details.field
      // (a business rule such as a duplicate code).
      if (error instanceof ApiError) {
        if (error.fieldErrors.length > 0) {
          error.fieldErrors.forEach((fieldError) =>
            setError(fieldError.field as Path<TValues>, { message: fieldError.message }),
          );
          return;
        }
        const field = error.details?.field;
        if (typeof field === 'string') {
          setError(field as Path<TValues>, { message: error.message });
          return;
        }
      }
      onOtherError(error);
    },
  });
}
